import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  RefreshCw,
  MapPin,
  Coins,
  TrendingUp,
  TrendingDown,
  CalendarDays,
  Download,
  Truck,
  Award,
  Info,
  PieChart,
} from 'lucide-react';
import {
  fetchShippingAnalytics,
  fetchShippingCarriers,
  type ShippingAnalyticsResponse,
  type MonthlyVolumeItem,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { Loading } from '../ui/feedback.js';
import { formatTnd } from '../lib/format.js';

// ---------------------------------------------------------------------------
// Date helpers — filter defaults to the current month (1st → today)
// ---------------------------------------------------------------------------

function toIsoDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function firstDayOfCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

function lastDayOfMonthOf(dateIso: string): string {
  const [y, m] = dateIso.split('-').map(Number);
  const last = new Date(y ?? 2026, (m ?? 1), 0).getDate();
  return `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
}

function shiftMonths(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y ?? 2026, (m ?? 1) - 1 + delta, d ?? 1);
  return toIsoDate(dt);
}

function formatPeriodLabel(start: string, end: string): string {
  const fmt = (iso: string) => {
    const dt = new Date(`${iso}T00:00:00`);
    return Number.isNaN(dt.getTime()) ? iso : dt.toLocaleDateString('fr-FR');
  };
  return `${fmt(start)} → ${fmt(end)}`;
}

function formatMonthShort(yyyyMm: string): string {
  const dt = new Date(`${yyyyMm}-01T00:00:00`);
  if (Number.isNaN(dt.getTime())) return yyyyMm;
  const s = dt.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatMonthLong(yyyyMm: string): string {
  const dt = new Date(`${yyyyMm}-01T00:00:00`);
  if (Number.isNaN(dt.getTime())) return yyyyMm;
  const s = dt.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function recoveryTone(pct: number): string {
  if (pct >= 90) return 'text-emerald-700';
  if (pct >= 75) return 'text-amber-700';
  return 'text-rose-700';
}

function recoveryFill(pct: number): string {
  if (pct >= 90) return 'bg-emerald-600';
  if (pct >= 75) return 'bg-amber-500';
  return 'bg-rose-500';
}

function currentYearMonth(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Tunisia governorates — schematic heat-map layout (24 gouvernorats)
// ---------------------------------------------------------------------------

interface GovGeo {
  id: string;
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  region: string;
}

const TUNISIA_GOVERNORATES: readonly GovGeo[] = [
  { id: 'bizerte', name: 'Bizerte', x: 80, y: 15, w: 55, h: 35, region: 'Nord' },
  { id: 'ariana', name: 'Ariana', x: 120, y: 48, w: 32, h: 22, region: 'Grand Tunis' },
  { id: 'tunis', name: 'Tunis', x: 132, y: 68, w: 30, h: 22, region: 'Grand Tunis' },
  { id: 'ben-arous', name: 'Ben Arous', x: 136, y: 88, w: 32, h: 24, region: 'Grand Tunis' },
  { id: 'la-manouba', name: 'La Manouba', x: 104, y: 62, w: 30, h: 26, region: 'Grand Tunis' },
  { id: 'nabeul', name: 'Nabeul', x: 165, y: 65, w: 55, h: 48, region: 'Cap Bon' },
  { id: 'zaghouan', name: 'Zaghouan', x: 110, y: 92, w: 40, h: 32, region: 'Nord-Est' },
  { id: 'beja', name: 'Béja', x: 50, y: 42, w: 40, h: 38, region: 'Nord-Ouest' },
  { id: 'jendouba', name: 'Jendouba', x: 15, y: 45, w: 40, h: 42, region: 'Nord-Ouest' },
  { id: 'le-kef', name: 'Le Kef', x: 15, y: 85, w: 42, h: 45, region: 'Nord-Ouest' },
  { id: 'siliana', name: 'Siliana', x: 65, y: 85, w: 42, h: 45, region: 'Nord-Ouest' },
  { id: 'sousse', name: 'Sousse', x: 145, y: 118, w: 40, h: 38, region: 'Sahel' },
  { id: 'monastir', name: 'Monastir', x: 175, y: 138, w: 35, h: 32, region: 'Sahel' },
  { id: 'mahdia', name: 'Mahdia', x: 160, y: 168, w: 42, h: 42, region: 'Sahel' },
  { id: 'kairouan', name: 'Kairouan', x: 95, y: 125, w: 48, h: 50, region: 'Centre' },
  { id: 'kasserine', name: 'Kasserine', x: 25, y: 135, w: 55, h: 62, region: 'Centre-Ouest' },
  { id: 'sidi-bouzid', name: 'Sidi Bouzid', x: 75, y: 175, w: 52, h: 58, region: 'Centre' },
  { id: 'sfax', name: 'Sfax', x: 125, y: 205, w: 65, h: 60, region: 'Centre-Est' },
  { id: 'gafsa', name: 'Gafsa', x: 35, y: 205, w: 55, h: 52, region: 'Sud-Ouest' },
  { id: 'tozeur', name: 'Tozeur', x: 15, y: 255, w: 52, h: 45, region: 'Sud-Ouest' },
  { id: 'kebili', name: 'Kébili', x: 50, y: 275, w: 65, h: 65, region: 'Sud-Ouest' },
  { id: 'gabes', name: 'Gabès', x: 115, y: 265, w: 55, h: 48, region: 'Sud-Est' },
  { id: 'medenine', name: 'Médenine', x: 135, y: 310, w: 65, h: 60, region: 'Sud-Est' },
  { id: 'tataouine', name: 'Tataouine', x: 95, y: 365, w: 85, h: 95, region: 'Sud' },
];

/** Accent-insensitive key so "Kébili" matches "Kebili", "Ben-Arous" matches "Ben Arous", … */
function normGov(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ');
}

const DONUT_COLORS = [
  '#047857', // emerald-700
  '#0284c7', // sky-600
  '#d97706', // amber-600
  '#e11d48', // rose-600
  '#7c3aed', // violet-600
  '#0d9488', // teal-600
  '#c026d3', // fuchsia-600
  '#4d7c0f', // lime-700
  '#1d4ed8', // blue-700
  '#94a3b8', // slate-400
] as const;

type CarrierSortKey = 'volume' | 'success' | 'returns' | 'cod';
type PresetKey = 'month' | 'last' | 'quarter' | 'ytd' | 'year' | 'custom';

const PRESETS: readonly { key: PresetKey; label: string }[] = [
  { key: 'month', label: 'Mois en cours' },
  { key: 'last', label: 'Mois dernier' },
  { key: 'quarter', label: '3 derniers mois' },
  { key: 'ytd', label: 'Année YTD' },
  { key: 'year', label: '12 mois' },
];

export function LogisticsAnalyticsPage(): React.ReactElement {
  const [selectedCarrier, setSelectedCarrier] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>(() => firstDayOfCurrentMonth());
  const [endDate, setEndDate] = useState<string>(() => toIsoDate(new Date()));
  const [preset, setPreset] = useState<PresetKey>('month');
  const [carrierSort, setCarrierSort] = useState<CarrierSortKey>('volume');
  const [selectedGov, setSelectedGov] = useState<string | null>(null);

  const carriersQuery = useQuery({
    queryKey: ['shipping', 'carriers'],
    queryFn: fetchShippingCarriers,
  });

  const analyticsQuery = useQuery({
    queryKey: ['shipping', 'analytics', selectedCarrier, startDate, endDate],
    queryFn: () => fetchShippingAnalytics({ carrier: selectedCarrier, startDate, endDate }),
  });

  const data: ShippingAnalyticsResponse | undefined = analyticsQuery.data;
  const periodLabel = useMemo(() => formatPeriodLabel(startDate, endDate), [startDate, endDate]);

  const applyPreset = (key: PresetKey): void => {
    setPreset(key);
    const today = toIsoDate(new Date());
    const firstThisMonth = firstDayOfCurrentMonth();
    if (key === 'month') {
      setStartDate(firstThisMonth);
      setEndDate(today);
    } else if (key === 'last') {
      const firstLast = shiftMonths(firstThisMonth, -1);
      setStartDate(firstLast);
      setEndDate(lastDayOfMonthOf(firstLast));
    } else if (key === 'quarter') {
      setStartDate(shiftMonths(firstThisMonth, -2));
      setEndDate(today);
    } else if (key === 'ytd') {
      setStartDate(`${today.slice(0, 4)}-01-01`);
      setEndDate(today);
    } else if (key === 'year') {
      setStartDate(shiftMonths(firstThisMonth, -11));
      setEndDate(today);
    }
  };

  const onCustomDate = (kind: 'start' | 'end', value: string): void => {
    setPreset('custom');
    if (kind === 'start') setStartDate(value);
    else setEndDate(value);
  };

  // ------------------------------------------------------------------ KPIs
  const totalParcels = data?.carriers.reduce((acc, c) => acc + c.total, 0) ?? 0;
  const deliveredParcels = data?.carriers.reduce((acc, c) => acc + c.delivered, 0) ?? 0;
  const returnParcels = data?.carriers.reduce((acc, c) => acc + c.returns, 0) ?? 0;
  const inTransitParcels = data?.carriers.reduce((acc, c) => acc + c.inTransit, 0) ?? 0;
  const totalCodManaged = data?.carriers.reduce((acc, c) => acc + c.totalCod, 0) ?? 0;
  const totalCodDelivered = data?.carriers.reduce((acc, c) => acc + c.deliveredCod, 0) ?? 0;

  const globalSuccessRate = totalParcels > 0 ? Math.round((deliveredParcels / totalParcels) * 1000) / 10 : 0;
  const globalReturnRate = totalParcels > 0 ? Math.round((returnParcels / totalParcels) * 1000) / 10 : 0;
  const avgCod = totalParcels > 0 ? totalCodManaged / totalParcels : 0;
  const recoveryRate = totalCodManaged > 0 ? Math.round((totalCodDelivered / totalCodManaged) * 1000) / 10 : 0;
  const assignedInScope = data?.meta.assignedInScope ?? totalParcels;
  const totalInScope = data?.meta.totalInScope ?? totalParcels;
  const assignmentRate = totalInScope > 0 ? Math.round((assignedInScope / totalInScope) * 1000) / 10 : 100;

  const sortedCarriers = useMemo(() => {
    const list = [...(data?.carriers ?? [])];
    if (carrierSort === 'success') list.sort((a, b) => b.deliveryRate - a.deliveryRate);
    else if (carrierSort === 'returns') list.sort((a, b) => b.returnRate - a.returnRate);
    else if (carrierSort === 'cod') list.sort((a, b) => b.deliveredCod - a.deliveredCod);
    else list.sort((a, b) => b.total - a.total);
    return list;
  }, [data?.carriers, carrierSort]);

  // Auto-insights (new): best / weakest carrier, momentum, concentration
  const insights = useMemo(() => {
    const out: string[] = [];
    // Significant volume only (≥30 colis and ≥1 % of the period): a 6-parcel
    // carrier at 100 % must not be crowned, nor flagged on a single return.
    // The two guards are mutually exclusive by construction — the "best"
    // carrier must have returns at/below average, so it can never also be
    // the returns outlier (the firas paradox: 89.5 % delivered + 10.5 % returns).
    const minInsightVolume = Math.max(30, Math.round(totalParcels * 0.01));
    const bestCandidates = (data?.carriers ?? []).filter(
      (c) => c.total >= minInsightVolume && c.deliveryRate > 0 && c.returnRate <= globalReturnRate,
    );
    if (bestCandidates.length > 0) {
      const best = [...bestCandidates].sort((a, b) => b.deliveryRate - a.deliveryRate)[0];
      if (best)
        out.push(
          `Meilleur taux de livraison : ${best.carrier} (${best.deliveryRate} % sur ${best.total.toLocaleString('fr-FR')} colis, retours maîtrisés à ${best.returnRate} %).`,
        );
    }
    const vigilanceCandidates = (data?.carriers ?? []).filter(
      (c) => c.total >= minInsightVolume && c.returnRate > globalReturnRate + 2,
    );
    if (vigilanceCandidates.length > 0) {
      const worst = [...vigilanceCandidates].sort((a, b) => b.returnRate - a.returnRate)[0];
      if (worst)
        out.push(
          `Vigilance retours : ${worst.carrier} culmine à ${worst.returnRate} % de retours (moyenne ${globalReturnRate} %) — à auditer en priorité.`,
        );
    }
    const months = [...(data?.monthlyVolume ?? [])].sort((a, b) => a.month.localeCompare(b.month));
    // Never compare a partial (in-progress) month against a complete one:
    // e.g. Oct 6 vs full September reads as "-79 %" but is just 6 days of data.
    // MoM momentum is computed on the last two COMPLETE months only; the
    // running month gets a neutral progress line instead of a false alarm.
    const now = new Date();
    const currentYm = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const complete = months.filter((m) => m.month < currentYm);
    const partial = months.find((m) => m.month >= currentYm);
    if (complete.length >= 2) {
      const prev = complete[complete.length - 2];
      const last = complete[complete.length - 1];
      if (prev && last && prev.count > 0) {
        const pct = Math.round(((last.count - prev.count) / prev.count) * 100);
        out.push(
          pct >= 0
            ? `Dynamique volume : +${pct} % entre ${formatMonthShort(prev.month)} et ${formatMonthShort(last.month)} (${last.count.toLocaleString('fr-FR')} colis, mois complets).`
            : `Dynamique volume : ${pct} % entre ${formatMonthShort(prev.month)} et ${formatMonthShort(last.month)} — creux à investiguer.`,
        );
      }
    }
    if (partial) {
      out.push(
        `Mois en cours (${formatMonthShort(partial.month)}, J+${now.getDate()}) : ${partial.count.toLocaleString('fr-FR')} colis — mois incomplet, exclu des comparaisons.`,
      );
    }
    if ((data?.topDestinations?.length ?? 0) > 0 && totalParcels > 0) {
      const top = data?.topDestinations[0];
      if (top) {
        const share = Math.round((top.count / totalParcels) * 1000) / 10;
        out.push(`Concentration géographique : ${top.gouvernerat} absorbe ${share} % des expéditions — sécuriser la capacité sur cet axe.`);
      }
    }
    if (totalInScope > assignedInScope) {
      out.push(
        `${(totalInScope - assignedInScope).toLocaleString('fr-FR')} colis de la période ne sont assignés à aucun transporteur — gisement d'expédition immédiate.`,
      );
    }
    return out;
  }, [data?.carriers, data?.monthlyVolume, data?.topDestinations, totalParcels, totalInScope, assignedInScope, globalReturnRate]);

  // ------------------------------------------------------- Monthly evolution
  const monthlyAsc = useMemo(() => {
    return [...(data?.monthlyVolume ?? [])].sort((a, b) => a.month.localeCompare(b.month));
  }, [data?.monthlyVolume]);
  const maxMonthlyCount = Math.max(...monthlyAsc.map((m) => m.count), 1);

  // ------------------------------------------------- YTD financial (exact)
  // Backend returns exact monthly SUM(prix) / SUM(prix of delivered) for
  // Jan → selected end. Delivered = middleware livré (2/10/32) OR carrier API
  // 'livr*' from our App DB — no estimation, no 0–5000 clipping: recovery and
  // MoM below are exact ratios of collected vs expected COD.
  const ytd = useMemo(() => {
    const items: readonly MonthlyVolumeItem[] = data?.ytdFinancial ?? [];
    let cumTotal = 0;
    let cumDelivered = 0;
    const rows = items.map((m, idx) => {
      cumTotal += m.totalCod;
      cumDelivered += m.deliveredCod;
      const prev = idx > 0 ? items[idx - 1] : undefined;
      const prevDelivered = prev?.deliveredCod ?? 0;
      const momPct = prev && prevDelivered > 0 ? Math.round(((m.deliveredCod - prevDelivered) / prevDelivered) * 100) : null;
      return { ...m, momPct, recoveryPct: m.totalCod > 0 ? Math.round((m.deliveredCod / m.totalCod) * 1000) / 10 : 0 };
    });
    const maxCod = Math.max(...items.map((i) => Math.max(i.totalCod, i.deliveredCod)), 1);
    const withCod = rows.filter((r) => r.totalCod > 0);
    const best = withCod.length > 0 ? [...withCod].sort((a, b) => b.recoveryPct - a.recoveryPct)[0] : undefined;
    const worst = withCod.length > 0 ? [...withCod].sort((a, b) => a.recoveryPct - b.recoveryPct)[0] : undefined;
    return {
      year: data?.meta.ytdYear ?? new Date().getFullYear(),
      rows,
      cumulativeTotal: cumTotal,
      cumulativeDelivered: cumDelivered,
      pendingCod: Math.max(0, cumTotal - cumDelivered),
      globalRecoveryPct: cumTotal > 0 ? Math.round((cumDelivered / cumTotal) * 1000) / 10 : 0,
      maxCod,
      best,
      worst,
    };
  }, [data?.ytdFinancial, data?.meta.ytdYear]);

  // ------------------------------------------------------- Destinations
  const destinationStats = useMemo(() => {
    const list = data?.topDestinations ?? [];
    const denom = totalParcels > 0 ? totalParcels : list.reduce((a, d) => a + d.count, 0) || 1;
    return list.map((d, i) => ({
      ...d,
      share: Math.round((d.count / denom) * 1000) / 10,
      color: DONUT_COLORS[i % DONUT_COLORS.length] ?? '#94a3b8',
    }));
  }, [data?.topDestinations, totalParcels]);

  const govLookup = useMemo(() => {
    const map = new Map<string, { count: number; totalCod: number; share: number; color: string; label: string }>();
    for (const d of destinationStats) map.set(normGov(d.gouvernerat), { count: d.count, totalCod: d.totalCod, share: d.share, color: d.color, label: d.gouvernerat });
    return map;
  }, [destinationStats]);

  const maxGovCount = Math.max(...destinationStats.map((d) => d.count), 1);
  const donutTotal = destinationStats.reduce((a, d) => a + d.count, 0);
  const donutCirc = 2 * Math.PI * 58;

  const statusRows = useMemo(() => {
    const list = [...(data?.statusDistribution ?? [])].sort((a, b) => b.count - a.count);
    const denom = list.reduce((a, s) => a + s.count, 0) || 1;
    return list.map((s) => ({ ...s, share: Math.round((s.count / denom) * 1000) / 10 }));
  }, [data?.statusDistribution]);

  const exportCarriersCsv = (): void => {
    const header = 'transporteur;colis;part_%;livres;taux_succes_%;retours;taux_retour_%;en_transit;cod_attendu;cod_encaisse;panier_moyen';
    const lines = sortedCarriers.map((c) => {
      const share = totalParcels > 0 ? Math.round((c.total / totalParcels) * 1000) / 10 : 0;
      const basket = c.total > 0 ? Math.round((c.totalCod / c.total) * 1000) / 1000 : 0;
      return [c.carrier, c.total, share, c.delivered, c.deliveryRate, c.returns, c.returnRate, c.inTransit, c.totalCod, c.deliveredCod, basket]
        .map((v) => String(v).replace(/;/g, ','))
        .join(';');
    });
    const blob = new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comparatif-transporteurs_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const heatFill = (count: number): { fill: string; stroke: string; text: string } => {
    const intensity = maxGovCount > 0 ? count / maxGovCount : 0;
    if (intensity > 0.6) return { fill: '#065f46', stroke: '#047857', text: '#ffffff' };
    if (intensity > 0.3) return { fill: '#059669', stroke: '#10b981', text: '#ffffff' };
    if (intensity > 0.12) return { fill: '#34d399', stroke: '#6ee7b7', text: '#064e3b' };
    if (intensity > 0.03) return { fill: '#a7f3d0', stroke: '#6ee7b7', text: '#064e3b' };
    if (count > 0) return { fill: '#d1fae5', stroke: '#a7f3d0', text: '#065f46' };
    return { fill: '#eef2f7', stroke: '#cbd5e1', text: '#94a3b8' };
  };

  return (
    <div className="space-y-6 animate-fade">
      <PageHeader
        index="S40"
        eyebrow="Gestion Logistique · Analytique"
        title="Analytique & Performance de Livraison"
        purpose="Périmètre = colis assignés à un transporteur sur la période. Taux de réussite (OTD), comparatif transporteurs, flux COD exacts et carte logistique."
        actions={
          <Button variant="outline" size="sm" onClick={() => void analyticsQuery.refetch()} disabled={analyticsQuery.isFetching}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${analyticsQuery.isFetching ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        }
      />
      
      {/* ---------- Filter bar : carrier + date range (default = current month) ---------- */}
      <Panel className="p-4 border-border bg-card shadow-xs">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-ink uppercase tracking-wider mr-1">
            <CalendarDays className="h-4 w-4 text-emerald-700" /> Période
          </span>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p.key)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors cursor-pointer ${
                preset === p.key
                  ? 'bg-emerald-700 text-white border-emerald-700 shadow-xs'
                  : 'bg-card text-ink-soft border-border hover:border-emerald-500 hover:text-emerald-800'
              }`}
            >
              {p.label}
            </button>
          ))}
          <div className="flex items-center gap-1.5 ml-1">
            <input
              type="date"
              value={startDate}
              max={endDate}
              onChange={(e) => e.target.value && onCustomDate('start', e.target.value)}
              className="text-xs bg-card border border-border rounded-lg px-2 py-1.5 text-ink font-mono focus:outline-none focus:ring-1 focus:ring-emerald-600"
              aria-label="Date de début"
            />
            <span className="text-xs text-ink-faint">→</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => e.target.value && onCustomDate('end', e.target.value)}
              className="text-xs bg-card border border-border rounded-lg px-2 py-1.5 text-ink font-mono focus:outline-none focus:ring-1 focus:ring-emerald-600"
              aria-label="Date de fin"
            />
          </div>
          <select
            value={selectedCarrier}
            onChange={(e) => setSelectedCarrier(e.target.value)}
            className="text-xs bg-card border border-border rounded-lg px-3 py-1.5 text-ink font-semibold shadow-xs cursor-pointer focus:outline-none focus:ring-1 focus:ring-emerald-600"
            aria-label="Transporteur"
          >
            <option value="all">Tous les transporteurs</option>
            {carriersQuery.data?.carriers
              .filter((c) => c.kind !== 'unassigned')
              .map((c) => (
                <option key={c.carrier} value={c.carrier}>
                  {c.carrier} ({c.count.toLocaleString('fr-FR')} colis)
                </option>
              ))}
          </select>
          <span className="ml-auto text-[11px] font-mono text-ink-soft bg-cream px-2.5 py-1 rounded-full border border-border">
            {periodLabel}
            {selectedCarrier !== 'all' ? ` · ${selectedCarrier}` : ''}
          </span>
        </div>
      </Panel>

      {analyticsQuery.isLoading ? (
        <div className="py-16 text-center">
          <Loading label="Calcul des indicateurs logistiques en cours…" />
        </div>
      ) : analyticsQuery.isError ? (
        <Panel className="p-8 text-center text-sm text-rose-700">
          Impossible de charger les analytiques sur {periodLabel}. Vérifiez la connexion puis réessayez.
        </Panel>
      ) : (
        <>
          {/* ---------- KPI row (6 cards) ---------- */}
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
            <Panel className="p-4 space-y-2 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-soft">Taux de succès</p>
              <p className="text-3xl font-extrabold font-mono text-emerald-700">{globalSuccessRate}%</p>
              <p className="text-[11px] text-ink-faint">{deliveredParcels.toLocaleString('fr-FR')} / {totalParcels.toLocaleString('fr-FR')} livrés</p>
              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div className="bg-emerald-600 h-1.5 rounded-full" style={{ width: `${Math.min(100, Math.max(2, globalSuccessRate))}%` }} />
              </div>
            </Panel>
            <Panel className="p-4 space-y-2 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-soft">Taux de retour</p>
              <p className="text-3xl font-extrabold font-mono text-rose-700">{globalReturnRate}%</p>
              <p className="text-[11px] text-ink-faint">{returnParcels.toLocaleString('fr-FR')} retours définitifs</p>
              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div className="bg-rose-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, Math.max(2, globalReturnRate))}%` }} />
              </div>
            </Panel>
            <Panel className="p-4 space-y-2 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-soft">En transit</p>
              <p className="text-3xl font-extrabold font-mono text-sky-800">{inTransitParcels.toLocaleString('fr-FR')}</p>
              <p className="text-[11px] text-ink-faint">En cours d'acheminement</p>
              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-sky-500 h-1.5 rounded-full"
                  style={{ width: `${totalParcels > 0 ? Math.min(100, Math.max(2, (inTransitParcels / totalParcels) * 100)) : 0}%` }}
                />
              </div>
            </Panel>
            <Panel className="p-4 space-y-2 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-soft">COD encaissé</p>
              <p className="text-lg font-extrabold font-mono text-ink truncate" title={formatTnd(totalCodDelivered)}>{formatTnd(totalCodDelivered)}</p>
              <p className="text-[11px] text-ink-faint truncate">sur {formatTnd(totalCodManaged)} · recouv. {recoveryRate}%</p>
              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div className="bg-emerald-600 h-1.5 rounded-full" style={{ width: `${Math.min(100, Math.max(2, recoveryRate))}%` }} />
              </div>
            </Panel>
            <Panel className="p-4 space-y-2 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-soft">Panier COD moyen</p>
              <p className="text-3xl font-extrabold font-mono text-ink">{avgCod.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} <span className="text-sm font-bold">DT</span></p>
              <p className="text-[11px] text-ink-faint">Par colis assigné</p>
              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div className="bg-amber-500 h-1.5 rounded-full" style={{ width: totalParcels > 0 ? '100%' : '0%' }} />
              </div>
            </Panel>
            <Panel className="p-4 space-y-2 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-soft">Taux d'affectation</p>
              <p className="text-3xl font-extrabold font-mono text-indigo-800">{assignmentRate}%</p>
              <p className="text-[11px] text-ink-faint">{assignedInScope.toLocaleString('fr-FR')} / {totalInScope.toLocaleString('fr-FR')} affectés</p>
              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div className="bg-indigo-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, Math.max(2, assignmentRate))}%` }} />
              </div>
            </Panel>
          </div>

          {/* ---------- Auto insights (new) ---------- */}
          {insights.length > 0 && (
            <Panel className="p-4 border-indigo-200 bg-indigo-50/40 flex gap-3">
              <Award className="h-5 w-5 text-indigo-700 shrink-0 mt-0.5" />
              <ul className="text-xs text-ink space-y-1">
                {insights.map((line, i) => (
                  <li key={i} className="leading-relaxed">• {line}</li>
                ))}
              </ul>
            </Panel>
          )}

          {/* ---------- 1. Carrier comparison — active carriers in period only ---------- */}
          <Panel className="p-5 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-emerald-700 text-white shadow-xs shrink-0">
                  <Truck className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-ink">Comparatif des Performances Transporteurs</h3>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-300">
                      {sortedCarriers.length} actif{sortedCarriers.length > 1 ? 's' : ''} · {periodLabel}
                    </span>
                  </div>
                  <p className="text-xs text-ink-soft mt-0.5">
                    Transporteurs ayant ≥ 1 colis assigné sur la période. Non-assignés, annulés (état 6) et états masqués exclus.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={carrierSort}
                  onChange={(e) => setCarrierSort(e.target.value as CarrierSortKey)}
                  className="text-xs bg-card border border-border rounded-lg px-2.5 py-1.5 font-semibold text-ink cursor-pointer"
                  aria-label="Tri du comparatif"
                >
                  <option value="volume">Tri : Volume</option>
                  <option value="success">Tri : Taux succès</option>
                  <option value="returns">Tri : Taux retour</option>
                  <option value="cod">Tri : COD encaissé</option>
                </select>
                <Button variant="outline" size="sm" onClick={exportCarriersCsv} disabled={sortedCarriers.length === 0}>
                  <Download className="h-3.5 w-3.5 mr-1" /> CSV
                </Button>
              </div>
            </div>

            {sortedCarriers.length === 0 ? (
              <p className="text-xs text-ink-faint italic py-8 text-center">
                Aucun transporteur actif sur {periodLabel} — élargissez la période ou vérifiez l'affectation des colis.
              </p>
            ) : (
              <TableShell plain>
                <Table>
                  <THead>
                    <Tr>
                      <Th>Transporteur</Th>
                      <Th align="right">Colis (part)</Th>
                      <Th align="right">Livrés · Succès</Th>
                      <Th align="right">Retours · Taux</Th>
                      <Th align="right">En transit</Th>
                      <Th align="right">COD attendu</Th>
                      <Th align="right">COD encaissé · Recouv.</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {sortedCarriers.map((c, idx) => {
                      const share = totalParcels > 0 ? Math.round((c.total / totalParcels) * 1000) / 10 : 0;
                      const recov = c.totalCod > 0 ? Math.round((c.deliveredCod / c.totalCod) * 1000) / 10 : 0;
                      return (
                        <Tr key={c.carrier}>
                          <Td>
                            <div className="flex items-center gap-2">
                              <span className={`h-5 w-5 rounded-md flex items-center justify-center font-mono text-[10px] font-bold ${idx < 3 ? 'bg-emerald-700 text-white' : 'bg-slate-100 text-slate-700'}`}>
                                {idx + 1}
                              </span>
                              <span className="font-semibold text-xs text-ink">{c.carrier}</span>
                            </div>
                          </Td>
                          <Td align="right">
                            <span className="font-mono text-xs font-bold text-ink">{c.total.toLocaleString('fr-FR')}</span>{' '}
                            <span className="font-mono text-[11px] text-ink-soft">({share}%)</span>
                          </Td>
                          <Td align="right">
                            <span className="font-mono text-xs text-emerald-800 font-semibold">{c.delivered.toLocaleString('fr-FR')}</span>{' '}
                            <span className="inline-flex font-mono text-[11px] font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                              {c.deliveryRate}%
                            </span>
                            <div className="w-20 bg-slate-100 rounded-full h-1 ml-auto mt-1 overflow-hidden">
                              <div className="bg-emerald-600 h-1 rounded-full" style={{ width: `${Math.min(100, c.deliveryRate)}%` }} />
                            </div>
                          </Td>
                          <Td align="right">
                            <span className="font-mono text-xs text-rose-800">{c.returns.toLocaleString('fr-FR')}</span>{' '}
                            <span className="font-mono text-[11px] text-rose-700 bg-rose-50 px-2 py-0.5 rounded-full border border-rose-200">{c.returnRate}%</span>
                          </Td>
                          <Td align="right">
                            <span className="font-mono text-xs text-sky-800 font-semibold">{c.inTransit.toLocaleString('fr-FR')}</span>
                          </Td>
                          <Td align="right"><span className="font-mono text-xs text-ink">{formatTnd(c.totalCod)}</span></Td>
                          <Td align="right">
                            <span className="font-mono text-xs font-bold text-emerald-800">{formatTnd(c.deliveredCod)}</span>{' '}
                            <span className="font-mono text-[11px] text-ink-soft">({recov}%)</span>
                          </Td>
                        </Tr>
                      );
                    })}
                  </TBody>
                </Table>
              </TableShell>
            )}
          </Panel>

          {/* ---------- 2. Shipment volume evolution — assigned only ---------- */}
          <Panel className="p-5 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-border">
              <div>
                <h3 className="text-base font-bold text-ink">Évolution du Volume d'Expéditions</h3>
                <p className="text-xs text-ink-soft">Colis assignés à un transporteur · {periodLabel} · livrés / retours / en cours.</p>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs">
                <span className="inline-flex items-center gap-1.5 text-emerald-700 font-medium"><span className="h-2.5 w-2.5 rounded-xs bg-emerald-600" /> Livrés</span>
                <span className="inline-flex items-center gap-1.5 text-rose-700 font-medium"><span className="h-2.5 w-2.5 rounded-xs bg-rose-500" /> Retours</span>
                <span className="inline-flex items-center gap-1.5 text-sky-700 font-medium"><span className="h-2.5 w-2.5 rounded-xs bg-sky-400" /> En cours / Autre</span>
              </div>
            </div>

            {monthlyAsc.length === 0 ? (
              <p className="text-xs text-ink-faint italic py-8 text-center">Aucune expédition assignée sur {periodLabel}.</p>
            ) : (
              <>
                <div className="flex items-end gap-2 sm:gap-3 pt-2 overflow-x-auto pb-1" role="img" aria-label="Volume mensuel des expéditions">
                  {monthlyAsc.map((m) => {
                    const hPct = Math.max(4, Math.round((m.count / maxMonthlyCount) * 100));
                    const delPct = m.count > 0 ? (m.delivered / m.count) * 100 : 0;
                    const retPct = m.count > 0 ? (m.returns / m.count) * 100 : 0;
                    const otherPct = Math.max(0, 100 - delPct - retPct);
                    const success = m.count > 0 ? Math.round((m.delivered / m.count) * 1000) / 10 : 0;
                    const nowYm = (() => {
                      const n = new Date();
                      return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
                    })();
                    const isPartial = m.month >= nowYm;
                    return (
                      <div key={m.month} className="flex flex-col items-center gap-1.5 min-w-[64px] flex-1">
                        <span className="font-mono text-[11px] font-bold text-ink">{m.count.toLocaleString('fr-FR')}</span>
                        <div
                          className="w-full max-w-[72px] bg-slate-100 rounded-lg flex flex-col-reverse overflow-hidden border border-slate-200/70"
                          style={{ height: `${Math.round(hPct * 1.6)}px`, minHeight: '40px', maxHeight: '176px', opacity: isPartial ? 0.65 : 1 }}
                          title={`${formatMonthLong(m.month)} : ${m.count} colis — ${m.delivered} livrés (${success} %), ${m.returns} retours, ${formatTnd(m.totalCod)}${isPartial ? ' — mois en cours, incomplet' : ''}`}
                        >
                          {delPct > 0 && <div className="bg-emerald-600 w-full" style={{ height: `${delPct}%` }} />}
                          {retPct > 0 && <div className="bg-rose-500 w-full" style={{ height: `${retPct}%` }} />}
                          {otherPct > 0 && <div className="bg-sky-400 w-full" style={{ height: `${otherPct}%` }} />}
                        </div>
                        <span className="font-mono text-[10px] text-ink-soft">{formatMonthShort(m.month)}</span>
                        <span className={`font-mono text-[10px] font-bold ${success >= 70 ? 'text-emerald-700' : success >= 40 ? 'text-amber-700' : 'text-rose-700'}`}>{success}%</span>
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
                  <div className="text-xs bg-cream/50 rounded-lg px-3 py-2 border border-border"><span className="text-ink-soft">Total période : </span><strong className="font-mono">{monthlyAsc.reduce((a, m) => a + m.count, 0).toLocaleString('fr-FR')} colis</strong></div>
                  <div className="text-xs bg-cream/50 rounded-lg px-3 py-2 border border-border"><span className="text-ink-soft">Moyenne / mois : </span><strong className="font-mono">{Math.round(monthlyAsc.reduce((a, m) => a + m.count, 0) / monthlyAsc.length).toLocaleString('fr-FR')}</strong></div>
                  <div className="text-xs bg-cream/50 rounded-lg px-3 py-2 border border-border"><span className="text-ink-soft">COD expédié : </span><strong className="font-mono">{formatTnd(monthlyAsc.reduce((a, m) => a + m.totalCod, 0))}</strong></div>
                  <div className="text-xs bg-cream/50 rounded-lg px-3 py-2 border border-border"><span className="text-ink-soft">COD encaissé : </span><strong className="font-mono text-emerald-800">{formatTnd(monthlyAsc.reduce((a, m) => a + m.deliveredCod, 0))}</strong></div>
                </div>
              </>
            )}
          </Panel>

          {/* ---------- 3. YTD financial flows — exact, date-filtered ---------- */}
          <Panel className="p-5 border-emerald-500/30 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-emerald-600 text-white shadow-xs shrink-0">
                  <Coins className="h-5 w-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-ink">
                      Flux Financiers COD : Comparatif Annuel YTD (Janvier → {formatMonthLong(ytd.rows[ytd.rows.length - 1]?.month ?? `${ytd.year}-01`)})
                    </h3>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-300">
                      {ytd.rows.length} mois · {ytd.year}
                    </span>
                  </div>
                  <p className="text-xs text-ink-soft mt-0.5">
                    Encaissé réel = Σ prix des livrés (états 2/10/32) · Attendu = Σ prix des assignés · calcul exact, piloté par la fin de période ({endDate}).
                    {selectedCarrier !== 'all' ? ` Filtré sur ${selectedCarrier}.` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span className="inline-flex items-center gap-1.5 text-slate-700 font-medium"><span className="h-3 w-3 rounded-xs bg-slate-400" /> Attendu</span>
                <span className="inline-flex items-center gap-1.5 text-emerald-700 font-bold"><span className="h-3 w-3 rounded-xs bg-emerald-600" /> Encaissé</span>
              </div>
            </div>

            {ytd.rows.length === 0 ? (
              <p className="text-xs text-ink-faint italic py-8 text-center">Aucun flux COD assigné depuis janvier {ytd.year}.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 rounded-xl border border-emerald-300 bg-emerald-50/50 space-y-1">
                    <span className="text-[11px] font-semibold text-emerald-800 uppercase tracking-wider block">Encaissé YTD</span>
                    <p className="text-xl font-extrabold text-emerald-950 font-mono">{formatTnd(ytd.cumulativeDelivered)}</p>
                    <p className="text-[11px] text-emerald-700">Fonds collectés depuis janvier</p>
                  </div>
                  <div className="p-4 rounded-xl border border-border bg-slate-50/50 space-y-1">
                    <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider block">Expédié YTD</span>
                    <p className="text-xl font-bold text-slate-900 font-mono">{formatTnd(ytd.cumulativeTotal)}</p>
                    <p className="text-[11px] text-ink-soft">Valeur totale assignée</p>
                  </div>
                  <div className="p-4 rounded-xl border border-indigo-200 bg-indigo-50/50 space-y-1">
                    <span className="text-[11px] font-semibold text-indigo-800 uppercase tracking-wider block">Recouvrement</span>
                    <p className="text-xl font-extrabold text-indigo-950 font-mono">{ytd.globalRecoveryPct}%</p>
                    <p className="text-[11px] text-indigo-700">Encaissé ÷ expédié (exact)</p>
                  </div>
                  <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/50 space-y-1">
                    <span className="text-[11px] font-semibold text-amber-800 uppercase tracking-wider block">Écart / en cours</span>
                    <p className="text-xl font-bold text-amber-950 font-mono">{formatTnd(ytd.pendingCod)}</p>
                    <p className="text-[11px] text-amber-700">Retours ou en attente</p>
                  </div>
                </div>

                {/* One-line verdict: the whole year in a sentence */}
                {ytd.best && ytd.worst && (
                  <p className="text-xs text-ink leading-relaxed bg-cream/60 border border-border rounded-xl px-3.5 py-2.5">
                    Recouvrement YTD <strong className={`font-mono ${recoveryTone(ytd.globalRecoveryPct)}`}>{ytd.globalRecoveryPct}%</strong>
                    {' '}· Top : <strong>{formatMonthShort(ytd.best.month)}</strong> <span className={`font-mono font-bold ${recoveryTone(ytd.best.recoveryPct)}`}>{ytd.best.recoveryPct}%</span>
                    {' '}· À surveiller : <strong>{formatMonthShort(ytd.worst.month)}</strong> <span className={`font-mono font-bold ${recoveryTone(ytd.worst.recoveryPct)}`}>{ytd.worst.recoveryPct}%</span>
                    {' '}· Écart restant <strong className="font-mono">{formatTnd(ytd.pendingCod)}</strong>
                  </p>
                )}

                <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 items-start">
                  {/* Combined chart: grey height = attendu, green fill = encaissé.
                      Fill ratio IS the recovery — one glance suffices. */}
                  <div className="xl:col-span-7 space-y-2">
                    <div className="flex items-end gap-2 sm:gap-2.5 h-48 pt-1" role="img" aria-label="Recouvrement COD mensuel">
                      {ytd.rows.map((m) => {
                        const barH = Math.max(5, Math.round((m.totalCod / ytd.maxCod) * 100));
                        const isPartial = m.month >= currentYearMonth();
                        return (
                          <div key={m.month} className="flex flex-col items-center gap-1 flex-1 min-w-0">
                            <div
                              className="w-full max-w-[52px] bg-slate-200 rounded-md flex flex-col-reverse overflow-hidden"
                              style={{ height: `${Math.round(barH * 1.5)}px`, maxHeight: '148px', opacity: isPartial ? 0.6 : 1 }}
                              title={`${formatMonthLong(m.month)} — attendu ${formatTnd(m.totalCod)}, encaissé ${formatTnd(m.deliveredCod)} (${m.recoveryPct} %)${isPartial ? ' — mois en cours' : ''}`}
                            >
                              <div className={`${recoveryFill(m.recoveryPct)} w-full`} style={{ height: `${Math.min(100, Math.max(m.totalCod > 0 ? 3 : 0, m.recoveryPct))}%` }} />
                            </div>
                            <span className="font-mono text-[10px] text-ink-soft truncate">{formatMonthShort(m.month)}{isPartial ? '*' : ''}</span>
                            <span className={`font-mono text-[11px] font-extrabold ${recoveryTone(m.recoveryPct)}`}>{m.recoveryPct}%</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink-soft px-1">
                      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-xs bg-slate-300" /> Hauteur = attendu</span>
                      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-xs bg-emerald-600" /> Remplissage = encaissé (≥90 %)</span>
                      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-xs bg-amber-500" /> 75–90 %</span>
                      <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-xs bg-rose-500" /> &lt;75 %</span>
                    </div>
                  </div>

                  {/* Dense figures table: exact numbers without scrolling */}
                  <div className="xl:col-span-5 overflow-x-auto rounded-xl border border-border">
                    <table className="w-full border-collapse text-[11px]">
                      <thead>
                        <tr className="bg-cream/90 border-b border-border text-ink-faint uppercase tracking-wider text-[10px]">
                          <th className="text-left font-semibold px-2.5 py-2">Mois</th>
                          <th className="text-right font-semibold px-2.5 py-2">Attendu</th>
                          <th className="text-right font-semibold px-2.5 py-2">Encaissé</th>
                          <th className="text-right font-semibold px-2.5 py-2">Recouv.</th>
                          <th className="text-right font-semibold px-2.5 py-2">MoM</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {ytd.rows.map((m) => (
                          <tr key={m.month} className="hover:bg-cream/40">
                            <td className="px-2.5 py-1.5 font-bold text-ink whitespace-nowrap">{formatMonthShort(m.month)}</td>
                            <td className="px-2.5 py-1.5 text-right font-mono text-ink-soft whitespace-nowrap">{formatTnd(m.totalCod)}</td>
                            <td className="px-2.5 py-1.5 text-right font-mono font-bold text-emerald-900 whitespace-nowrap">{formatTnd(m.deliveredCod)}</td>
                            <td className={`px-2.5 py-1.5 text-right font-mono font-extrabold whitespace-nowrap ${recoveryTone(m.recoveryPct)}`}>{m.recoveryPct}%</td>
                            <td className="px-2.5 py-1.5 text-right whitespace-nowrap">
                              {m.momPct === null ? (
                                <span className="font-mono text-ink-faint">—</span>
                              ) : (
                                <span className={`inline-flex items-center gap-0.5 font-mono font-bold ${m.momPct >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                  {m.momPct >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                                  {m.momPct >= 0 ? `+${m.momPct}` : m.momPct}%
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <p className="text-[11px] text-ink-faint flex items-start gap-1.5">
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  Montants exacts Σ(prix), sans écrêtage. Livré = middleware (2/10/32) OU API transporteur (carrierStatus « livr* »).
                  Recouvrement = encaissé ÷ attendu · MoM sur l'encaissé.
                </p>
              </>
            )}
          </Panel>

          {/* ---------- 4. Regions : heat map + donut ---------- */}
          <Panel className="p-5 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-border">
              <div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-emerald-700" />
                  <h3 className="text-base font-bold text-ink">Top 10 Régions & Cartographie Logistique (Tunisie)</h3>
                </div>
                <p className="text-xs text-ink-soft mt-0.5">Assignés sur {periodLabel} · cliquez une région ou un segment pour détailler.</p>
              </div>
              {selectedGov && (
                <button type="button" onClick={() => setSelectedGov(null)} className="text-xs text-emerald-800 font-semibold hover:underline cursor-pointer">
                  Réinitialiser la sélection
                </button>
              )}
            </div>

            {destinationStats.length === 0 ? (
              <p className="text-xs text-ink-faint italic py-8 text-center">Aucune destination sur {periodLabel}.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                {/* Heat map */}
                <div className="bg-gradient-to-b from-slate-50 to-emerald-50/40 p-5 rounded-2xl border border-slate-200/90 flex flex-col items-center space-y-4">
                  <div className="w-full flex items-center justify-between text-xs text-slate-600 px-1 flex-wrap gap-2">
                    <span className="font-bold flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-600" /> Carte thermique d'activité</span>
                    <div className="flex items-center gap-1 text-[10px]" aria-label="Légende d'intensité">
                      <span className="text-slate-400">Faible</span>
                      {['#d1fae5', '#a7f3d0', '#34d399', '#059669', '#065f46'].map((c) => (
                        <span key={c} className="h-2.5 w-5 rounded" style={{ backgroundColor: c }} />
                      ))}
                      <span className="text-emerald-950 font-bold">Forte</span>
                    </div>
                  </div>

                  <div className="w-full max-w-[300px] aspect-[250/470] relative">
                    <svg viewBox="0 0 250 470" className="w-full h-full drop-shadow-sm" role="img" aria-label="Carte thermique de la Tunisie par gouvernorat">
                      {TUNISIA_GOVERNORATES.map((g) => {
                        const stat = govLookup.get(normGov(g.name)) ?? govLookup.get(g.id);
                        const count = stat?.count ?? 0;
                        const { fill, stroke, text } = heatFill(count);
                        const isSelected = selectedGov !== null && normGov(selectedGov) === normGov(stat?.label ?? g.name) && count > 0;
                        const isTop3 = stat !== undefined && destinationStats.slice(0, 3).some((d) => normGov(d.gouvernerat) === normGov(stat.label));
                        return (
                          <g
                            key={g.id}
                            className="cursor-pointer transition-all duration-150 hover:opacity-85"
                            onClick={() => {
                              if (!stat) return;
                              setSelectedGov(isSelected ? null : stat.label);
                            }}
                          >
                            <title>{stat ? `${stat.label} — ${count.toLocaleString('fr-FR')} colis (${stat.share} %) · ${formatTnd(stat.totalCod)}` : `${g.name} — 0 colis`}</title>
                            <rect
                              x={g.x}
                              y={g.y}
                              width={g.w}
                              height={g.h}
                              rx={8}
                              fill={isSelected ? '#f59e0b' : fill}
                              stroke={isSelected ? '#b45309' : stroke}
                              strokeWidth={isSelected || isTop3 ? 2.5 : 1.2}
                              style={isTop3 && !isSelected ? { filter: 'drop-shadow(0 1px 3px rgba(4,120,87,.45))' } : undefined}
                            />
                            <text
                              x={g.x + g.w / 2}
                              y={g.y + g.h / 2 - (g.h > 40 && count > 0 ? 2 : 0) + 3}
                              textAnchor="middle"
                              fontSize={g.h < 26 ? 7.5 : 9}
                              fontWeight={count > 0 ? 'bold' : 'normal'}
                              fill={isSelected ? '#451a03' : text}
                              className="select-none pointer-events-none"
                            >
                              {g.h < 26 ? g.name.slice(0, 6) : g.name}
                            </text>
                            {g.h > 40 && count > 0 && (
                              <text
                                x={g.x + g.w / 2}
                                y={g.y + g.h / 2 + 11}
                                textAnchor="middle"
                                fontSize={8}
                                fontWeight="bold"
                                fill={isSelected ? '#451a03' : text}
                                className="select-none pointer-events-none font-mono"
                              >
                                {count >= 1000 ? `${(count / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })}k` : count} · {stat?.share ?? 0}%
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </svg>
                  </div>

                  <div className="w-full bg-white p-3 rounded-xl border border-slate-200 text-xs shadow-2xs">
                    {selectedGov && govLookup.get(normGov(selectedGov)) ? (
                      <div className="space-y-1">
                        {(() => {
                          const s = govLookup.get(normGov(selectedGov ?? ''))!;
                          return (
                            <>
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-slate-900">{s.label}</span>
                                <span className="text-[11px] font-mono font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded">{s.share}% du total</span>
                              </div>
                              <div className="flex items-center justify-between text-slate-600 font-mono text-[11px]">
                                <span>{s.count.toLocaleString('fr-FR')} colis</span>
                                <span>{formatTnd(s.totalCod)}</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-[11px] text-center italic">
                        {govLookup.size} gouvernorat{govLookup.size > 1 ? 's' : ''} desservis · {donutTotal.toLocaleString('fr-FR')} colis au top 10 — survolez ou cliquez pour détailler.
                      </p>
                    )}
                  </div>
                </div>

                {/* Donut (camembert) + ranking */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-xs text-ink-soft">
                    <PieChart className="h-4 w-4 text-emerald-700" />
                    <span className="font-semibold uppercase tracking-wider">Classement des destinations — camembert</span>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-5">
                    <div className="relative shrink-0">
                      <svg width="168" height="168" viewBox="0 0 168 168" role="img" aria-label="Répartition des destinations">
                        <circle cx="84" cy="84" r="58" fill="none" stroke="#eef2f7" strokeWidth="24" />
                        <g transform="rotate(-90 84 84)">
                          {(() => {
                            let acc = 0;
                            return destinationStats.map((d) => {
                              const frac = donutTotal > 0 ? d.count / donutTotal : 0;
                              const len = Math.max(0, frac * donutCirc - (destinationStats.length > 1 ? 1.5 : 0));
                              const offset = -acc;
                              acc += frac * donutCirc;
                              const active = selectedGov !== null && normGov(selectedGov) === normGov(d.gouvernerat);
                              return (
                                <circle
                                  key={d.gouvernerat}
                                  cx="84"
                                  cy="84"
                                  r="58"
                                  fill="none"
                                  stroke={active ? '#f59e0b' : d.color}
                                  strokeWidth={active ? 28 : 24}
                                  strokeDasharray={`${len} ${donutCirc - len}`}
                                  strokeDashoffset={offset}
                                  className="cursor-pointer transition-all"
                                  onClick={() => setSelectedGov(active ? null : d.gouvernerat)}
                                >
                                  <title>{`${d.gouvernerat} — ${d.count.toLocaleString('fr-FR')} colis (${d.share} %)`}</title>
                                </circle>
                              );
                            });
                          })()}
                        </g>
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="font-mono font-extrabold text-xl text-ink">{donutTotal.toLocaleString('fr-FR')}</span>
                        <span className="text-[10px] text-ink-soft uppercase tracking-wider">colis top 10</span>
                      </div>
                    </div>
                    <div className="flex-1 w-full space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
                      {destinationStats.map((d, idx) => {
                        const active = selectedGov !== null && normGov(selectedGov) === normGov(d.gouvernerat);
                        return (
                          <button
                            key={d.gouvernerat}
                            type="button"
                            onClick={() => setSelectedGov(active ? null : d.gouvernerat)}
                            className={`w-full flex items-center gap-2.5 p-2 rounded-lg border text-left transition-colors cursor-pointer ${active ? 'bg-amber-50 border-amber-400' : 'bg-card border-border hover:bg-cream/50'}`}
                          >
                            <span className="h-3.5 w-3.5 rounded-full shrink-0 border border-black/10" style={{ backgroundColor: d.color }} />
                            <span className="font-mono text-[10px] text-ink-faint w-6">#{idx + 1}</span>
                            <span className="text-xs font-bold text-ink flex-1 truncate">{d.gouvernerat}</span>
                            <span className="font-mono text-[11px] font-bold text-emerald-800">{d.share}%</span>
                            <span className="font-mono text-[11px] text-ink-soft">{d.count.toLocaleString('fr-FR')}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="rounded-xl border border-border bg-cream/40 p-3 text-[11px] text-ink-soft leading-relaxed">
                    Lecture : chaque part = colis du gouvernorat ÷ total assigné de la période ({totalParcels.toLocaleString('fr-FR')}).
                    Le top 3 concentre {destinationStats.slice(0, 3).reduce((a, d) => a + d.share, 0).toLocaleString('fr-FR')} % des volumes —
                    priorisez la capacité transporteur sur ces axes.
                  </div>
                </div>
              </div>
            )}
          </Panel>

          {/* ---------- 5. Status breakdown (new) ---------- */}
          {statusRows.length > 0 && (
            <Panel className="p-5 space-y-4">
              <div className="pb-2 border-b border-border">
                <h3 className="text-base font-bold text-ink">Répartition par Statut (colis assignés · {periodLabel})</h3>
                <p className="text-xs text-ink-soft mt-0.5">États middleware des expéditions confiées aux transporteurs — CFR suivi opérationnel.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                {statusRows.map((s) => (
                  <div key={s.etat} className="p-3 rounded-xl border border-border bg-card space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-bold text-ink truncate">{s.label} <span className="font-mono text-[10px] text-ink-faint">#{s.etat}</span></span>
                      <span className="font-mono font-bold text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200 text-[11px]">{s.share}%</span>
                    </div>
                    <div className="flex items-center justify-between font-mono text-[11px] text-ink-soft">
                      <span>{s.count.toLocaleString('fr-FR')} colis</span>
                      <span>{formatTnd(s.totalCod)}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                      <div className="bg-emerald-600 h-1.5 rounded-full" style={{ width: `${Math.min(100, Math.max(2, s.share))}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          <p className="text-[11px] text-ink-faint leading-relaxed px-1">
            Périmètre certifié : colis avec presta ou livreur renseigné, créés entre {periodLabel}
            {selectedCarrier !== 'all' ? `, transporteur = ${selectedCarrier}` : ', tous transporteurs actifs'}.
            Exclus : non-assignés (entrepôt), annulés (état 6) et états masqués par la configuration logistique.
            Livré = miroir middleware (états 2/10/32 : {(data?.meta.deliveredViaMiddleware ?? 0).toLocaleString('fr-FR')})
            OU API transporteur live en base (carrierStatus « livr* » : {(data?.meta.deliveredViaCarrierApi ?? 0).toLocaleString('fr-FR')}) —
            vérifiés API : {(data?.meta.jetpackChecked ?? 0).toLocaleString('fr-FR')} colis
            ({totalInScope > 0 ? Math.round(((data?.meta.jetpackChecked ?? 0) / totalInScope) * 1000) / 10 : 0} %).
            COD = prix middleware exact Σ(prix) — sans écrêtage ni estimation.
          </p>
        </>
      )}
    </div>
  );
}
